/**
 * Layar crossplot — lintas-sumur.
 *
 * Yang berdiri di sini sebelumnya adalah crossplot sesumur: dua kurva dari
 * sumur yang sedang aktif, satu zona pada satu waktu, ramp warna yang
 * dituliskan di dalam kode, dan histogram sebesar telapak tangan di sudut
 * kanan bawah tanpa satu pun kendali. Itu cukup untuk memandang sebuah sumur,
 * dan tidak cukup untuk pertanyaan yang sebenarnya membuat orang membuka
 * crossplot: *apakah lapangan ini berperilaku seperti satu batuan*. Awan
 * densitas–neutron enam sumur yang bertumpuk rapi dan awan yang terbelah dua
 * menjawab pertanyaan itu dengan dua jawaban yang berlawanan, dan tidak satu
 * pun dari keduanya terlihat pada plot yang hanya memuat satu sumur.
 *
 * Karena itu layar ini memplot daftar sumur, dan tiap titik membawa asalnya —
 * sumur mana, zona mana — supaya warnanya bisa menyatakan asal itu. Empat cara
 * membaca yang sama pentingnya berdiri berdampingan:
 *
 * - **titik atau kerapatan.** Di atas beberapa puluh ribu sampel, awan titik
 *   menjadi bidang pekat yang bentuknya ditentukan oleh tepinya saja. Peta
 *   kerapatan dihitung server dari seluruh data, bukan dari contoh yang
 *   tergambar.
 * - **poligon.** Membaca crossplot adalah melingkari sekelompok titik dan
 *   bertanya "yang ini apa" — bukan memandanginya saja.
 * - **regresi**, dengan R², dipasang pada seluruh data dan digambar hanya
 *   sepanjang data yang memang ada.
 * - **sumbu dan warna yang bisa disetel** — batas diketik sendiri, log atau
 *   linier, ramp dari pustaka yang sama dengan layar model 3D.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../api";
import { ClusterSectionView } from "../components/ClusterSection";
import { ColorBarEditor } from "../components/ColorBar";
import {
  CrossplotPolygons,
  onTheseAxes,
  useCrossplotPolygons,
} from "../components/CrossplotPolygons";
import {
  DataScope,
  DEFAULT_SCOPE,
  effectiveScope,
  normalizeScope,
  scopeParams,
  ScopeEmpty,
  wellModeOf,
} from "../components/DataScope";
import { HistogramChart } from "../components/HistogramChart";
import { LogSource } from "../components/LogSource";
import { PickettPanel } from "../components/PickettPlot";
import { RailCollapseButton, RailStrip, type RailTabItem } from "../components/RailTabs";
import { Resizer } from "../components/Resizer";
import {
  Button,
  Dropdown,
  Empty,
  KeyValue,
  Loading,
  Modal,
  Notice,
  OptionalNumberField,
  Panel,
  Segmented,
  Slider,
  Toggle,
} from "../components/ui";
import { useAsync, useRampsVersion, useStored, useWellSync } from "../hooks";
import { defaultScale, makePainter, type ColorScale } from "../lib/colorscale";
import {
  adaptAxes,
  adaptOne,
  curveOptions,
  useAdaptOnNewScope,
  useLastDefined,
  usePreferredCurve,
} from "../lib/curveoptions";
import { count } from "../lib/plural";
import { coordinateStep, coordinateTicks, logTicks, tickDecimals } from "../lib/ticks";
import {
  distinctColors,
  fitSegment,
  marginals,
  PLOT_PAD as PAD,
  PLOT_SPAN as SPAN,
  pointInPolygon,
  polygonPath,
  shapeVertices,
  summarise,
  valueToVertex,
  vertexToValue,
  type Vertex,
} from "../lib/xplot";
import {
  dragShape,
  insertVertex,
  midpoints,
  moveVertex,
  nearestHandle,
  removeVertex,
  thinPath,
  type ShapeTool,
} from "../lib/xshapes";
import type {
  CrossplotPolygon,
  FieldCrossplot,
  FieldHistogram,
  Project,
  Well,
} from "../types";

/**
 * Kurva yang lazim digambar menaik ke bawah pada sumbu tegak crossplot.
 *
 * Chart densitas–neutron dicetak dengan RHOB 1,9 di puncak dan 2,9 di dasar di
 * mana pun ia dicetak, dan sudut serpihnya karena itu ada di kanan bawah.
 * Aturannya milik kurvanya dan bukan milik pasangannya: sebuah log densitas
 * dibaca menaik ke bawah terhadap apa pun yang dipasangkan dengannya.
 */
const Y_DOWNWARD = new Set(["RHOB", "RHOZ", "RHOM", "DEN", "DENS"]);

/** Kurva neutron, penghuni sumbu mendatar chart densitas–neutron. */
const NEUTRON = new Set(["NPHI", "TNPH", "NPOR", "CNL", "PHIN"]);

/**
 * Benar hanya untuk pasangan yang memang chart densitas–neutron.
 *
 * Garis matriks dan garis serpih pernah digambar untuk pasangan kurva apa pun.
 * Pada GR–PHIE keduanya tetap muncul lengkap dengan namanya, menamai dua garis
 * yang tidak berarti apa-apa di sana — dan keterangan yang salah lebih buruk
 * daripada tidak ada keterangan, sebab ia dipercaya.
 */
function isDensityNeutron(x: string, y: string): boolean {
  return NEUTRON.has(x.toUpperCase()) && Y_DOWNWARD.has(y.toUpperCase());
}

const RAIL_TABS: RailTabItem<"crossplot">[] = [{ key: "crossplot", label: "Crossplot" }];

/**
 * Sejauh mana kursor boleh meleset dan masih menunjuk sebuah titik, dalam piksel.
 *
 * Titik awannya sendiri berjari-jari sekitar satu satuan viewBox — beberapa
 * piksel — dan menuntut kursor mendarat persis di atasnya berarti pembacaan
 * yang hanya muncul kalau beruntung. Terlalu longgar juga tidak berguna: di
 * tengah awan yang pekat, lingkaran selebar setengah sentimeter selalu memuat
 * sesuatu, dan yang dilaporkannya bukan titik yang dimaksud melainkan titik
 * yang kebetulan terdekat.
 */
const HOVER_RADIUS = 11;

type ColourBy = "curve" | "well" | "zone" | "depth";
type PlotMode = "points" | "density";

/**
 * Ramp bawaan pewarnaan menurut kedalaman.
 *
 * Tidak lewat `defaultScale`, sebab yang di sana memilih menurut *kurva* dan
 * kedalaman bukan kurva. Yang dipakai ramp yang seragam secara persepsi:
 * pertanyaan yang dijawab pewarnaan ini adalah "apakah kedua kaki awan ini
 * berasal dari kedalaman yang berbeda", dan ramp yang terangnya melompat-lompat
 * membuat batas yang tidak ada di dalam datanya.
 */
const DEPTH_RAMP: ColorScale = { ramp: "viridis", reverse: false, min: null, max: null };

/* Kotak gambarnya — disisakan 3 dari 100 di tiap tepi — datang dari
   `lib/xplot`, dan bukan dari sepasang angka yang berdiri sendiri di sini.
   Angka itu dipakai dua kali: sekali untuk menggambar, sekali lagi untuk
   mengubah simpul poligon menjadi nilai kurva. Dua salinan yang berselisih
   satu satuan akan menggeser tiap poligon tersimpan sepersekian persen dari
   lebar plot — cukup untuk memindahkan sampel di tepi seleksi, dan tidak
   cukup untuk terlihat oleh siapa pun. */

export default function Crossplot({
  project,
  well,
  wells,
  dataVersion = 0,
  onDataChanged,
  onWellChange,
}: {
  project: Project;
  well: Well;
  /** Seluruh sumur lapangan — plot ini memilih sendiri mana yang masuk. */
  wells: Well[];
  /** Changes when the project's curves have been edited elsewhere. */
  dataVersion?: number;
  /**
   * Dipanggil ketika lingkup layar ini menyempit menjadi satu sumur, supaya
   * pemilih WELL di cangkang menunjuk sumur yang sama. Lihat `useWellSync`.
   */
  onWellChange?: (wellId: number) => void;
  /**
   * Dipanggil ketika layar ini sendiri menulis kurva — poligon yang
   * diterbitkan sebagai kelas. Pohon sumur dan daftar set kurva di layar lain
   * membaca dari muatan yang sudah terlanjur dimuat, dan set baru yang tidak
   * mengabarkan dirinya baru muncul setelah seseorang memuat ulang halaman.
   */
  onDataChanged?: () => void;
}) {
  const navigate = useNavigate();
  const [railWidth, setRailWidth] = useStored("evolog.xplot.rail", 300);
  const [railCollapsed, setRailCollapsed] = useStored("evolog.xplot.rail.collapsed", false);

  // Lingkup dibuka pada sumur yang sedang aktif dan bukan pada seluruh
  // lapangan: seseorang yang berpindah ke layar ini datang dari sebuah sumur,
  // dan awan enam sumur yang muncul tanpa diminta menjawab pertanyaan yang
  // belum ditanyakannya. Menambah sumur lain kemudian adalah satu klik.
  // Dibaca lewat `normalizeScope`: lingkup menetap di peramban, dan bentuk
  // lama yang masih tersimpan di sana menyebut selangnya dengan kata-kata yang
  // sudah tidak dipakai lagi — dibiarkan apa adanya, kuerinya berangkat tanpa
  // selang sama sekali.
  const [storedScope, setScope] = useStored<unknown>(`evolog.xplot.scope.${project.id}`, {
    ...DEFAULT_SCOPE,
    wellMode: "single",
    depthMode: "all",
    wells: [well.id],
  });
  const scope = useMemo(() => normalizeScope(storedScope), [storedScope]);

  /* Lingkup berisi tepat satu sumur berarti layar ini sedang berdiri di atas
     sumur itu, dan pemilih di cangkang menunjuknya. Kosong berarti *seluruh*
     sumur lapangan — bukan satu — jadi ia ikut menjadi null di sini bersama
     pilihan berisi enam. */
  // Satu sumur — Tunggal, atau Multi yang tinggal satu centang — berarti layar
  // ini berdiri di atas sumur itu; Radius dan Multi berisi banyak tidak.
  const singleWell = wellModeOf(scope) !== "radius" && scope.wells.length === 1 ? scope.wells[0] : null;
  useWellSync(singleWell, well.id, {
    onFocus: onWellChange,
    onAdopt: (wellId) => setScope({ ...scope, wells: [wellId] }),
  });

  // Pilihan tersimpan dan sumbu yang dipakai dipisah: sumbu menyesuaikan diri
  // dengan sumur yang dipilih, pilihan orang tetap (lib/curveoptions.ts).
  const x = usePreferredCurve(useStored("evolog.xplot.x", "NPHI"));
  const y = usePreferredCurve(useStored("evolog.xplot.y", "RHOB"));
  const z = usePreferredCurve(useStored("evolog.xplot.z", "VSH"));
  const [xCurve, setXCurve] = [x.value, x.choose];
  const [yCurve, setYCurve] = [y.value, y.choose];
  const [zCurve, setZCurve] = [z.value, z.choose];
  const [colourBy, setColourBy] = useStored<ColourBy>("evolog.xplot.colourby", "curve");
  const [scale, setScale] = useStored<ColorScale>("evolog.xplot.scale", defaultScale("VSH"));
  const [logX, setLogX] = useStored("evolog.xplot.logx", false);
  const [logY, setLogY] = useStored("evolog.xplot.logy", false);
  const [xMin, setXMin] = useStored<number | null>("evolog.xplot.xmin", null);
  const [xMax, setXMax] = useStored<number | null>("evolog.xplot.xmax", null);
  const [yMin, setYMin] = useStored<number | null>("evolog.xplot.ymin", null);
  const [yMax, setYMax] = useStored<number | null>("evolog.xplot.ymax", null);
  const [mode, setMode] = useStored<PlotMode>("evolog.xplot.mode", "points");
  const [pointSize, setPointSize] = useStored("evolog.xplot.dot", 1.05);
  const [showFit, setShowFit] = useStored("evolog.xplot.fit", false);
  const [showTemplate, setShowTemplate] = useStored("evolog.xplot.template", true);
  const [depthScale, setDepthScale] = useStored<ColorScale>("evolog.xplot.depthscale", DEPTH_RAMP);
  const [showEdges, setShowEdges] = useStored("evolog.xplot.edges", true);
  const hist = usePreferredCurve(useStored("evolog.xplot.hist", "PHIE"));
  const [histCurve, setHistCurve] = [hist.value, hist.choose];

  // Poligon seleksi. Tidak disimpan sebagai pecahan kotak gambar: sebuah
  // lingkaran yang digambar di atas awan kemarin dan muncul kembali di atas
  // awan hari ini melingkari titik yang sama sekali lain. Yang ingin disimpan
  // orang berpindah ke `CrossplotPolygons`, tempat bentuknya diubah lebih dulu
  // menjadi nilai kurva — dan nilai kurva menunjuk batuan yang sama selamanya.
  const [polygon, setPolygon] = useState<Vertex[]>([]);
  const [drawing, setDrawing] = useState(false);
  const [tool, setTool] = useStored<ShapeTool>("evolog.xplot.tool", "polygon");
  // Kelas tersimpan yang sedang disunting simpulnya, bila ada.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [sectionOpen, setSectionOpen] = useState(false);
  const [showSaved, setShowSaved] = useStored("evolog.xplot.saved", true);

  const saved = useCrossplotPolygons(project.id);

  // Plot Pickett dicocokkan dengan tangan, dan menyeret garis di dalam kolom
  // selebar tiga ratus piksel berarti menyeretnya sejauh dua ratus. Jendela
  // penuh memuat satu panel yang sama — bukan panel kedua dengan keadaannya
  // sendiri, sebab dua garis Archie yang berbeda di dua tempat pada satu layar
  // adalah dua jawaban untuk satu pertanyaan.
  const [pickettFull, setPickettFull] = useState(false);

  const axisParams = {
    log_x: logX ? "1" : undefined,
    log_y: logY ? "1" : undefined,
    x_min: xMin ?? undefined,
    x_max: xMax ?? undefined,
    y_min: yMin ?? undefined,
    y_max: yMax ?? undefined,
  };

  const plot = useAsync(
    (signal) =>
      api.get<FieldCrossplot>(
        `/api/projects/${project.id}/crossplot/`,
        {
          x: xCurve,
          y: yCurve,
          z: colourBy === "curve" ? zCurve : undefined,
          ...scopeParams(scope),
          ...axisParams,
        },
        signal,
      ),
    [project.id, xCurve, yCurve, zCurve, colourBy, scope, logX, logY, xMin, xMax, yMin, yMax,
     dataVersion],
  );

  const histogram = useAsync(
    (signal) =>
      api.get<FieldHistogram>(
        `/api/projects/${project.id}/histogram/`,
        { curve: histCurve, bins: 20, ...scopeParams(scope) },
        signal,
      ),
    [project.id, histCurve, scope, dataVersion],
  );

  // Poligon menjadi tidak berarti begitu sumbunya berpindah: yang dilingkari
  // adalah kedudukan pada kotak gambar, bukan nilai. Membiarkannya berdiri di
  // atas sumbu yang baru berarti melaporkan statistik sekelompok titik yang
  // tidak pernah dipilih siapa pun.
  //
  // `dataVersion` ikut di sini sebab sumbu bisa berpindah tanpa satu pun dari
  // yang lain berubah. Kurva yang tidak punya skala tetap di `CURVE_SCALES` —
  // mnemonic vendor mana pun — memakai batas yang dihitung dari sebaran
  // datanya sendiri, dan satu LAS baru yang masuk lewat layar lain menggeser
  // batas itu di bawah poligon yang sedang tergambar. Bentuknya tetap di
  // tempatnya, batuan yang dikurungnya yang berganti, dan `vertexToValue`
  // menyimpannya lewat sumbu yang baru.
  useEffect(() => {
    setPolygon([]);
    setDrawing(false);
    setEditingId(null);
  }, [xCurve, yCurve, logX, logY, xMin, xMax, yMin, yMax, scope, dataVersion]);

  /* Sumbu dihitung di sini dan bukan di dalam yang menggambar.
     Tiga tempat menanyakan hal yang sama — awan titiknya, ringkasan seleksi,
     dan bentuk poligon tersimpan yang harus dipasang kembali ke kotaknya — dan
     tiga jawaban yang dihitung sendiri-sendiri adalah tiga kesempatan untuk
     berselisih. Yang berselisih di sini tidak terlihat sebagai galat: poligon
     tetap tergambar rapi, hanya saja di atas batuan yang lain. */
  const invertY = Y_DOWNWARD.has(yCurve.toUpperCase());
  const fixedX = useMemo(
    (): [number, number] | null =>
      xMin !== null && xMax !== null ? [xMin, xMax] : plot.data?.x_scale ?? null,
    [xMin, xMax, plot.data?.x_scale],
  );
  const fixedY = useMemo(
    (): [number, number] | null =>
      yMin !== null && yMax !== null ? [yMin, yMax] : plot.data?.y_scale ?? null,
    [yMin, yMax, plot.data?.y_scale],
  );
  const axes = useMemo(() => {
    const points = plot.data?.points;
    if (!points) return null;
    const ax = buildAxis(points.x, logX, false, fixedX);
    const ay = buildAxis(points.y, logY, invertY, fixedY);
    return ax && ay ? { x: ax, y: ay } : null;
  }, [plot.data?.points, logX, logY, invertY, fixedX, fixedY]);

  /* Hanya poligon yang berdiri di atas sepasang sumbu yang sedang tampil.
     Sebuah bentuk NPHI–RHOB yang digambar ulang di atas GR–RT tetap tergambar
     rapi sebagai bentuk yang tidak melingkari apa pun yang dimaksud siapa pun. */
  const onPlot = useMemo(
    () => (showSaved ? onTheseAxes(saved.data ?? [], xCurve, yCurve) : []),
    [showSaved, saved.data, xCurve, yCurve],
  );

  const available = plot.data?.available ?? [];
  // Pilihan sumbu adalah semua kurva proyek, bukan hanya lingkup: lingkup satu
  // sumur tanpa kurva terpilih dulu mengunci layar ini (lib/curveoptions.ts).
  const projectCurves =
    useLastDefined(plot.data?.curves ?? histogram.data?.curves) ?? available;
  const options = useMemo(
    () => curveOptions(projectCurves, plot.data?.available, [xCurve, yCurve, zCurve, histCurve]),
    [projectCurves, plot.data?.available, xCurve, yCurve, zCurve, histCurve],
  );
  const curveNames = useMemo(() => options.map((option) => option.value), [options]);

  // Sumbu mengikuti sumur: begitu lingkup baru tidak memuat sumbu terpilih —
  // GR–PHIE pada sumur Teapot yang belum dievaluasi — sumbunya berpindah ke
  // pasangan yang ada, alih-alih menggambar layar kosong sampai orang
  // menebak kurva mana yang dimiliki sumur itu.
  // Dihitung dari pilihan tersimpan, bukan dari sumbu sumur sebelumnya.
  useAdaptOnNewScope(plot.data, plot.loading, JSON.stringify(scope), () => {
    const here = plot.data?.available ?? [];
    const [nx, ny] = adaptAxes(here, x.preferred, y.preferred) ?? [x.preferred, y.preferred];
    x.use(nx);
    y.use(ny);
    if (colourBy === "curve") z.use(adaptOne(here, z.preferred, ["VSH", "GR", "CALI"]) ?? z.preferred);
  });
  useAdaptOnNewScope(histogram.data, histogram.loading, JSON.stringify(scope), () => {
    hist.use(adaptOne(histogram.data?.available ?? [], hist.preferred, ["PHIE", "PHIT", "GR"])
             ?? hist.preferred);
  });

  // Warna sumur berasal dari statusnya, jadi tiga sumur minyak membawa satu
  // hijau yang sama dan sumur rencana membawa "none". Keduanya benar di peta
  // dan keduanya tidak bisa dibaca sebagai legenda di sini.
  const wellColors = useMemo(
    () => distinctColors((plot.data?.wells ?? []).map((row) => row.color)),
    [plot.data?.wells],
  );

  const zoneColors = useMemo(() => {
    const map = new Map<string, string>();
    for (const zone of plot.data?.zones ?? []) map.set(zone.name, zone.color);
    return map;
  }, [plot.data?.zones]);

  // `versiRamp`: warna palet yang disunting di tempat ikut tergambar.
  const versiRamp = useRampsVersion();
  const painter = useMemo(() => {
    const points = plot.data?.points;
    return makePainter(scale, points?.z_min ?? 0, points?.z_max ?? 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, plot.data?.points, versiRamp]);

  /* Pelukis kedalaman berdiri sendiri, dan rentangnya diambil dari kedalaman
     yang benar-benar terpakai — bukan dari nol sampai TD. Sebuah lapangan yang
     datanya hanya di dua ratus meter terbawah dan diwarnai dari permukaan
     menghabiskan seluruh rampnya untuk kedalaman yang tidak menyumbang satu
     titik pun, dan awan yang tersisa tergambar dengan dua warna. */
  const depthPainter = useMemo(() => {
    const range = plot.data?.md_range;
    return makePainter(depthScale, range?.[0] ?? 0, range?.[1] ?? 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depthScale, plot.data?.md_range, versiRamp]);

  /** Warna sebuah titik menurut cara membaca yang sedang dipilih. */
  const colourFor = useMemo(() => {
    const points = plot.data?.points;
    return (index: number): string => {
      if (!points) return "var(--accent)";
      if (colourBy === "well") return wellColors[points.well[index]] ?? "#8b939e";
      if (colourBy === "zone") return zoneColors.get(points.zone[index] ?? "") ?? "#8b939e";
      if (colourBy === "depth") {
        const md = points.md?.[index];
        return md === null || md === undefined ? "#8b939e" : depthPainter.at(md);
      }
      const z = points.z?.[index];
      return z === null || z === undefined ? "#8b939e" : painter.at(z);
    };
  }, [plot.data, colourBy, painter, depthPainter, zoneColors, wellColors]);

  return (
    <div style={{ display: "flex", alignItems: "stretch", flex: 1, minHeight: 0 }}>
      <section
        style={{
          flex: 1,
          minWidth: 0,
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 9 }}>
          <span style={{ font: "600 11px var(--font-sans)", color: "var(--ink)" }}>
            {yCurve} – {xCurve}
          </span>
          <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
            {plot.data?.wells.length ?? 0} wells
            {/* Pada peta kerapatan yang diwarnai adalah cacah sampel per sel,
                jadi pemilih warna di rel tidak sedang memerintah apa pun. Kepala
                yang tetap menulis "coloured by well" di atas satu ramp cokelat
                menyebutkan pengaturan, bukan gambar — dan yang dibaca orang di
                kepala sebuah plot adalah keterangan tentang gambarnya. */}
            {mode === "density"
              ? " · coloured by samples per cell"
              : plot.data?.z_label && colourBy === "curve"
                ? ` · coloured by ${plot.data.z_label}`
                : colourBy === "well"
                  ? " · coloured by well"
                  : colourBy === "zone"
                    ? " · coloured by zone"
                    : colourBy === "depth"
                      ? " · coloured by depth"
                      : ""}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
            {plot.data ? `${plot.data.points.count.toLocaleString("en-US")} points` : ""}
            {/* "5.995 drawn" adalah kabar tentang pencuplikan awan titik. Peta
                kerapatan tidak menggambar satu titik pun — ia dihitung server
                atas seluruh sampel — jadi menyebutkan cuplikan di sana
                mengabarkan pengurangan yang tidak terjadi. */}
            {mode === "points"
              && plot.data && plot.data.points.shown < plot.data.points.count
              ? ` · ${plot.data.points.shown.toLocaleString("en-US")} drawn`
              : ""}
          </span>
        </div>

        {plot.loading && !plot.data && <Loading label="Menghitung crossplot…" />}
        {plot.error && <Notice tone="error">{plot.error}</Notice>}
        {plot.data && plot.data.points.count === 0 && (
          <ScopeEmpty skipped={plot.data.skipped} subject="points" />
        )}
        {plot.data && plot.data.points.count > 0 && (
          <>
            <ScatterView
              payload={plot.data}
              xLabel={xCurve}
              yLabel={yCurve}
              axes={axes}
              // Kelas yang sedang disunting tergambar sebagai bentuk suntingan saja.
              saved={editingId === null ? onPlot : onPlot.filter((p) => p.id !== editingId)}
              overlay={
                showTemplate && isDensityNeutron(xCurve, yCurve) ? "density-neutron" : "none"
              }
              mode={mode}
              pointSize={pointSize}
              colourFor={colourFor}
              densityColour={(t) => painter.atFraction(t)}
              showFit={showFit}
              showEdges={showEdges}
              polygon={polygon}
              drawing={drawing}
              tool={tool}
              onVertex={(vertex) => setPolygon((current) => [...current, vertex])}
              onClosePolygon={() => setDrawing(false)}
              onPolygonChange={(next, finished) => {
                setPolygon(next);
                if (finished) setDrawing(false);
              }}
            />
            <Legend
              payload={plot.data}
              colourBy={colourBy}
              zoneColors={zoneColors}
              wellColors={wellColors}
              scale={scale}
              depthScale={depthScale}
              mode={mode}
              densityColour={(t) => painter.atFraction(t)}
            />
          </>
        )}
      </section>

      {/* Kolom ini yang bergulir, bukan panel di dalamnya. Wadah yang membatasi
          tingginya sendiri lalu menggulir isinya membuat tiap kendali di dalamnya
          menyusut lebih dulu — dan yang paling pendek menyusut sampai habis:
          pemilih "Seluruh sumur / Selang marker" tinggal segaris enam piksel
          yang tidak bisa ditekan siapa pun. */}
      <section
        className="scroll-y"
        style={{
          width: 366,
          flex: "none",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 14,
          borderLeft: "1px solid var(--line-soft)",
        }}
      >
        {/* Pickett berdiri sendiri: lingkupnya sendiri, garisnya yang bisa
            diseret, dan parameternya sendiri. Rw sebuah lapisan bukan Rw
            lapangan, jadi ia tidak bisa ikut lingkup layar ini begitu saja —
            ia hanya berangkat dari sana. */}
        {!pickettFull && (
          <PickettPanel
            project={project}
            wells={wells}
            available={curveNames}
            dataVersion={dataVersion}
            activeWellId={well.id}
            onToggleExpand={() => setPickettFull(true)}
          />
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 7, flex: "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ font: "600 11px var(--font-sans)", color: "var(--ink)" }}>Histogram</span>
            <span style={{ flex: 1 }} />
            <div style={{ width: 104 }}>
              <Dropdown
                value={histCurve}
                options={options.length ? options : [{ value: histCurve, label: histCurve }]}
                onChange={setHistCurve}
                width="100%"
                compact
              />
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate(`/projects/${project.id}/histogram`)}
              title="Open the histogram screen, with its bin, group and statistics controls"
              style={{ height: 22, font: "500 10px var(--font-sans)" }}
            >
              Full screen
            </Button>
          </div>
          {histogram.error && <Notice tone="error">{histogram.error}</Notice>}
          {histogram.data ? (
            <>
              <HistogramChart data={histogram.data} height={104} />
              {histogram.data.total && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
                    n {histogram.data.total.n.toLocaleString("en-US")}
                  </span>
                  <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-5)" }}>
                    mean {histogram.data.total.mean.toFixed(3)}
                  </span>
                  <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
                    p50 {histogram.data.total.p50.toFixed(3)}
                  </span>
                </div>
              )}
            </>
          ) : (
            !histogram.loading && <Empty>No curve is available for a histogram.</Empty>
          )}
        </div>
      </section>

      {!railCollapsed && (
        <Resizer
          width={railWidth}
          onChange={setRailWidth}
          min={250}
          max={560}
          label="Resize the crossplot panel"
        />
      )}
      {railCollapsed ? (
        <RailStrip
          tabs={RAIL_TABS}
          active="crossplot"
          onSelect={() => setRailCollapsed(false)}
          expandLabel="Show the crossplot panel"
        />
      ) : (
        <aside
          style={{
            width: railWidth,
            flex: "none",
            background: "var(--rail)",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "4px 7px 4px 13px",
              borderBottom: "1px solid var(--line-soft)",
              flex: "none",
            }}
          >
            <span className="label">Crossplot</span>
            <RailCollapseButton
              side="right"
              collapsed={false}
              label="Hide the crossplot panel"
              onClick={() => setRailCollapsed(true)}
            />
          </div>
          <div
            className="scroll-y"
            style={{ flex: 1, padding: "14px 13px", display: "flex", flexDirection: "column", gap: 12 }}
          >
            <LogSource
              title="Sumber log — Crossplot"
              wells={wells}
              scope={scope}
              onChange={setScope}
              activeWellId={well.id}
              screening={plot.data?.screening}
              curves={
                <>
                  <LabelledDropdown label="X axis" value={xCurve} options={options} onChange={setXCurve} />
                  <LabelledDropdown label="Y axis" value={yCurve} options={options} onChange={setYCurve} />
                  <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>Skala warna</span>
                  <Segmented
                    value={colourBy}
                    size="sm"
                    options={[
                      { value: "curve", label: "Curve" },
                      { value: "well", label: "Well" },
                      { value: "zone", label: "Zone" },
                      { value: "depth", label: "Depth" },
                    ]}
                    onChange={(value) => setColourBy(value as ColourBy)}
                  />
                  {colourBy === "curve" && (
                    <LabelledDropdown
                      label="Colour curve (Z)"
                      value={zCurve}
                      options={options}
                      onChange={(value) => {
                        setZCurve(value);
                        setScale(defaultScale(value));
                      }}
                    />
                  )}
                </>
              }
            />

            <Panel title="Axes">
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <OptionalNumberField label="X MIN" value={xMin} onChange={setXMin} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <OptionalNumberField label="X MAX" value={xMax} onChange={setXMax} />
                </div>
              </div>
              <Toggle label="Logarithmic X axis" checked={logX} onChange={setLogX} />

              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <OptionalNumberField label="Y MIN" value={yMin} onChange={setYMin} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <OptionalNumberField label="Y MAX" value={yMax} onChange={setYMax} />
                </div>
              </div>
              <Toggle label="Logarithmic Y axis" checked={logY} onChange={setLogY} />
              {/* Batas yang dikosongkan bukan berarti tanpa batas: yang berlaku
                  kemudian adalah skala cetak kurva itu, dan barulah rentang
                  datanya sendiri. */}
              <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                A bound left blank follows the curve's print scale, then its data range.
              </span>
            </Panel>

            <DataScope
              wells={wells}
              scope={scope}
              onChange={setScope}
              skipped={plot.data?.skipped}
              screening={plot.data?.screening}
              selection={false}
            />

            <Panel title="Colour">
              {colourBy === "curve" && (
                <>
                  {plot.data?.points.z && (
                    <ColorBarEditor
                      property={zCurve}
                      scale={scale}
                      onChange={setScale}
                      dataMin={plot.data.points.z_min ?? 0}
                      dataMax={plot.data.points.z_max ?? 1}
                    />
                  )}
                </>
              )}
              {colourBy === "depth" && (
                <>
                  <ColorBarEditor
                    property="MD"
                    scale={depthScale}
                    onChange={setDepthScale}
                    dataMin={plot.data?.md_range?.[0] ?? 0}
                    dataMax={plot.data?.md_range?.[1] ?? 1}
                  />
                  {/* Kedalaman terukur, dan bukan TVD. Keduanya sama pada sumur
                      tegak dan berjauhan pada sumur miring, dan yang menamai
                      tiap sampel di seluruh modul ini adalah MD-nya. Legenda
                      yang diam soal itu terbaca sebagai TVD oleh siapa pun yang
                      membandingkan dua sumur berbeda kemiringan. */}
                  <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                    Measured depth, and the range is the depth the samples actually occupy —
                    not the well from surface to TD.
                  </span>
                </>
              )}
              {(colourBy === "well" || colourBy === "zone") && (
                <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                  The colour belongs to the {colourBy === "well" ? "well" : "zone"} itself — the
                  same one the correlation sheet and the map use, so that one thing never
                  changes colour from one screen to the next.
                </span>
              )}
            </Panel>

            <Panel title="Display">
              <Segmented
                value={mode}
                size="sm"
                options={[
                  { value: "points", label: "Points" },
                  { value: "density", label: "Density" },
                ]}
                onChange={(value) => setMode(value as PlotMode)}
              />
              {mode === "points" ? (
                <Slider
                  label="Point size"
                  value={pointSize}
                  min={0.4}
                  max={3}
                  step={0.05}
                  onChange={setPointSize}
                  format={(v) => v.toFixed(2)}
                />
              ) : (
                <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                  {plot.data?.density
                    ? `${plot.data.density.bins}×${plot.data.density.bins} cells from ${plot.data.density.used.toLocaleString("en-US")} samples — computed over all the data, not over the points drawn.`
                    : "No sample to compute from yet."}
                </span>
              )}
              <Toggle label="Regression line" checked={showFit} onChange={setShowFit} />
              <Toggle label="Edge histograms" checked={showEdges} onChange={setShowEdges} />
              {showEdges && (
                <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                  Summed from the density grid, so the bars stand on the same bins as the map
                  and their heights come from every sample — not from the cloud that is drawn.
                </span>
              )}
              <Toggle
                label="Matrix & shale lines"
                checked={showTemplate}
                onChange={setShowTemplate}
                disabled={!isDensityNeutron(xCurve, yCurve)}
              />
              {!isDensityNeutron(xCurve, yCurve) && (
                <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                  The lithology lines hold for a neutron–density pair only; on any other pair
                  they would name two lines that mean nothing.
                </span>
              )}
            </Panel>

            <Panel title="Cluster — seleksi titik">
              <Segmented
                value={tool}
                size="sm"
                options={[
                  { value: "polygon", label: "Poligon" },
                  { value: "circle", label: "Lingkaran" },
                  { value: "ellipse", label: "Elips" },
                  { value: "rect", label: "Kotak" },
                  { value: "freehand", label: "Bebas" },
                ]}
                onChange={(value) => {
                  setTool(value as ShapeTool);
                  // Berganti alat berarti menggambar bentuk baru dengan alat itu.
                  setPolygon([]);
                  setEditingId(null);
                  setDrawing(true);
                }}
              />
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <Button
                  variant={drawing ? "primary" : "ghost"}
                  onClick={() => {
                    setDrawing((current) => !current);
                    if (!drawing) {
                      setPolygon([]);
                      setEditingId(null);
                    }
                  }}
                  style={{ height: 24, font: "500 10px var(--font-sans)" }}
                >
                  {drawing ? "Selesai" : "Gambar"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setPolygon([]);
                    setDrawing(false);
                    setEditingId(null);
                  }}
                  disabled={!polygon.length}
                  style={{ height: 24, font: "500 10px var(--font-sans)" }}
                >
                  Hapus
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setSectionOpen(true)}
                  disabled={!plot.data || (polygon.length < 3 && onPlot.length === 0)}
                  title="Penampang sumur yang diwarnai menurut cluster"
                  style={{ height: 24, font: "500 10px var(--font-sans)" }}
                >
                  Penampang cluster
                </Button>
              </div>
              <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                {drawing
                  ? {
                      polygon: "Klik di plot untuk menaruh sudut, lalu Selesai (atau klik ganda). Tiga sudut sudah cukup.",
                      circle: "Tekan di pusat lingkaran, seret ke tepinya, lepaskan.",
                      ellipse: "Seret sebuah kotak; elipsnya tertulis di dalamnya.",
                      rect: "Seret dari satu sudut ke sudut seberangnya.",
                      freehand: "Tekan dan seret mengelilingi titik-titiknya; bentuknya menutup saat dilepas.",
                    }[tool]
                  : polygon.length >= 3
                    ? "Sunting bentuknya langsung di plot: seret simpul untuk menggeser, seret titik tengah sisi untuk menambah simpul, klik ganda simpul untuk menghapusnya."
                    : "Pilih alat lalu gambar di plot. Bentuk yang jadi bisa disunting, disimpan sebagai kelas, dan dilihat penampangnya."}
              </span>
              <SelectionStats
                payload={plot.data ?? null}
                polygon={polygon}
                axes={axes}
                xLabel={xCurve}
                yLabel={yCurve}
              />
              {polygon.length > 0 && polygon.length < 3 && (
                <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
                  {count(polygon.length, "corner")} — not an area yet.
                </span>
              )}
            </Panel>

            {/* Poligon yang tersimpan berdiri di panel tersendiri, dan bukan di
                dalam panel seleksi di atasnya. Keduanya memang menggambar bentuk
                yang sama, tetapi yang satu bertanya "yang ini apa" dan hilang
                bersama halamannya, sementara yang lain menerbitkan kurva ke
                sumur orang lain. Satu panel yang memuat keduanya membuat tombol
                kedua terbaca seperti kelanjutan tombol pertama. */}
            <CrossplotPolygons
              project={project}
              polygons={saved.data ?? []}
              reload={saved.reload}
              xCurve={xCurve}
              yCurve={yCurve}
              logX={logX}
              logY={logY}
              axes={axes}
              drawn={polygon}
              onSaved={() => {
                setPolygon([]);
                setDrawing(false);
              }}
              editingId={editingId}
              onEdit={(saved) => {
                if (!axes) return;
                setPolygon(saved.vertices.map(([vx, vy]) => valueToVertex(vx, vy, axes.x, axes.y)));
                setEditingId(saved.id);
                setDrawing(false);
              }}
              onEditDone={() => {
                setPolygon([]);
                setEditingId(null);
              }}
              wellIds={
                // Pada mode radius sumurnya adalah yang terjaring, bukan daftar centang.
                wellModeOf(scope) === "radius"
                  ? (plot.data?.wells ?? []).map((w) => w.id)
                  : effectiveScope(scope).wells
              }
              show={showSaved}
              onShowChange={setShowSaved}
              onPublished={onDataChanged}
            />

            <Panel title="Statistics">
              {plot.data ? (
                <>
                  <KeyValue k="Points" v={plot.data.points.count.toLocaleString("en-US")} />
                  <KeyValue
                    k={`${xCurve} mean`}
                    v={plot.data.x_stats ? plot.data.x_stats.mean.toFixed(3) : "—"}
                  />
                  <KeyValue
                    k={`${xCurve} p10–p90`}
                    v={
                      plot.data.x_stats
                        ? `${plot.data.x_stats.p10.toFixed(3)} – ${plot.data.x_stats.p90.toFixed(3)}`
                        : "—"
                    }
                  />
                  <KeyValue
                    k={`${yCurve} mean`}
                    v={plot.data.y_stats ? plot.data.y_stats.mean.toFixed(3) : "—"}
                  />
                  <KeyValue
                    k={`${yCurve} p10–p90`}
                    v={
                      plot.data.y_stats
                        ? `${plot.data.y_stats.p10.toFixed(3)} – ${plot.data.y_stats.p90.toFixed(3)}`
                        : "—"
                    }
                  />
                  <KeyValue
                    k="Correlation r"
                    v={plot.data.correlation === null ? "—" : plot.data.correlation.toFixed(3)}
                  />
                  {plot.data.regression && (
                    <>
                      <KeyValue
                        k="Regression"
                        v={`y = ${plot.data.regression.slope.toFixed(4)}x ${
                          plot.data.regression.intercept >= 0 ? "+" : "−"
                        } ${Math.abs(plot.data.regression.intercept).toFixed(4)}`}
                      />
                      <KeyValue
                        k="R²"
                        v={
                          plot.data.regression.r2 === null
                            ? "—"
                            : plot.data.regression.r2.toFixed(4)
                        }
                      />
                    </>
                  )}
                  {(plot.data.filter.vsh_max !== null || plot.data.filter.sw_min !== null) && (
                    <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                      {plot.data.filter.after.toLocaleString("en-US")} of{" "}
                      {plot.data.filter.before.toLocaleString("en-US")} points pass the filter.
                      Filtered before decimation, so the count comes from the data and not from the sample.
                    </span>
                  )}
                </>
              ) : (
                <Empty>Nothing has been computed yet.</Empty>
              )}
            </Panel>
          </div>
        </aside>
      )}

      {sectionOpen && (
        <Modal
          title={`Penampang cluster — ${xCurve}–${yCurve}`}
          width={1200}
          onClose={() => setSectionOpen(false)}
        >
          <ClusterSectionView
            projectId={project.id}
            wells={wells}
            params={{
              x: xCurve,
              y: yCurve,
              log_x: logX ? "1" : undefined,
              log_y: logY ? "1" : undefined,
              // Bentuk yang sedang digambar ikut sebagai "Seleksi", dalam nilai kurva.
              drawn:
                polygon.length >= 3 && axes
                  ? JSON.stringify(polygon.map((v) => vertexToValue(v, axes.x, axes.y)))
                  : undefined,
              ...scopeParams(scope),
            }}
          />
        </Modal>
      )}

      {pickettFull && (
        <Modal title="Pickett" width={1040} onClose={() => setPickettFull(false)}>
          <PickettPanel
            project={project}
            wells={wells}
            available={curveNames}
            dataVersion={dataVersion}
            activeWellId={well.id}
            expanded
            onToggleExpand={() => setPickettFull(false)}
          />
        </Modal>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- Sumbu */

interface Axis {
  lo: number;
  hi: number;
  log: boolean;
  invert: boolean;
  ticks: { value: number; t: number }[];
  decimals: number;
}

/**
 * Sumbu dari sederet nilai.
 *
 * `t` adalah kedudukan sebuah tanda pada sumbunya, 0 di pangkal dan 1 di ujung,
 * dan pembalikan sudah termasuk di dalamnya — supaya yang menggambar titik dan
 * yang menulis angka tidak pernah bisa berbeda pendapat tentang di mana sebuah
 * nilai berada.
 *
 * Skala cetak menang atas rentang datanya: chart densitas–neutron dibaca dengan
 * menaruh awan di atas garis litologi yang tercetak, dan itu hanya berlaku
 * kalau sumbunya selalu membentang sejauh yang sama. Skala yang menyentuh nol
 * tidak bisa dipakai sumbu logaritmik — log10(0) adalah minus tak hingga yang
 * menjalar ke setiap titik sebagai NaN — jadi ujung yang tidak positif diganti
 * nilai positif terkecil yang benar-benar ada pada datanya.
 */
function buildAxis(
  values: number[],
  log: boolean,
  invert: boolean,
  fixed?: [number, number] | null,
): Axis | null {
  const mapped = log ? values.map((v) => (v > 0 ? Math.log10(v) : NaN)) : values;
  const finite = mapped.filter((v) => Number.isFinite(v));
  let bounds: [number, number] | null = null;
  if (fixed && !log) {
    bounds = fixed;
  } else if (fixed) {
    const positives = values.filter((v) => v > 0 && Number.isFinite(v));
    const floor = positives.length ? Math.min(...positives) : 0;
    const low = fixed[0] > 0 ? fixed[0] : floor;
    const high = fixed[1] > 0 ? fixed[1] : floor;
    if (low > 0 && high > low) bounds = [Math.log10(low), Math.log10(high)];
  }
  if (!bounds && !finite.length) return null;
  const lo = bounds ? Math.min(...bounds) : Math.min(...finite);
  const hi = bounds ? Math.max(...bounds) : Math.max(...finite);
  const span = hi - lo || 1;
  const step = coordinateStep(span, 6);
  const raw = log ? logTicks(10 ** lo, 10 ** hi) : coordinateTicks(lo, hi, step);
  const ticks = raw.map((value) => {
    const position = ((log ? Math.log10(value) : value) - lo) / span;
    return { value, t: invert ? 1 - position : position };
  });
  return { lo, hi, log, invert, ticks, decimals: log ? 0 : tickDecimals(step) };
}

/** Kedudukan sebuah nilai pada sumbunya, 0..1, pembalikan sudah termasuk. */
function place(axis: Axis, value: number): number {
  const mapped = axis.log ? Math.log10(value) : value;
  const position = (mapped - axis.lo) / (axis.hi - axis.lo || 1);
  return axis.invert ? 1 - position : position;
}

/** Kedudukan nilai yang *sudah* dipetakan — grid kerapatan dan regresi. */
function placeMapped(axis: Axis, mapped: number): number {
  const position = (mapped - axis.lo) / (axis.hi - axis.lo || 1);
  return axis.invert ? 1 - position : position;
}

/**
 * Nilai sebuah kurva sebagaimana ia dibaca di pembacaan titik.
 *
 * Angka penting dan bukan desimal tetap: satu layar yang sama memuat NPHI
 * 0,2314 dan RT 148,6, dan tiga angka di belakang koma menulis yang kedua
 * sebagai 148,600 — ketelitian yang tidak pernah diukur siapa pun — sementara
 * nol desimal menulis yang pertama sebagai 0.
 */
function formatReading(value: number): string {
  if (!Number.isFinite(value)) return "—";
  const size = Math.abs(value);
  if (size === 0) return "0";
  if (size >= 100) return value.toFixed(0);
  if (size >= 1) return value.toFixed(2);
  return value.toPrecision(3);
}

function formatTick(axis: Axis, value: number): string {
  // Sumbu log melompati besaran, jadi angka desimalnya milik tiap tanda dan
  // bukan milik sumbunya: 0,2 dan 20 tidak bisa ditulis dengan aturan yang sama.
  const decimals = axis.log ? tickDecimals(Math.abs(value) || 1) : axis.decimals;
  return value.toFixed(decimals);
}

/* -------------------------------------------------------------- Gambar */

function ScatterView({
  payload,
  xLabel,
  yLabel,
  axes,
  saved,
  overlay,
  mode,
  pointSize,
  colourFor,
  densityColour,
  showFit,
  showEdges,
  polygon,
  drawing,
  tool,
  onVertex,
  onClosePolygon,
  onPolygonChange,
}: {
  payload: FieldCrossplot;
  xLabel: string;
  yLabel: string;
  /** Sumbu yang dihitung induknya — lihat alasannya di sana. */
  axes: { x: Axis; y: Axis } | null;
  /** Poligon tersimpan yang berdiri di atas sepasang sumbu ini. */
  saved: CrossplotPolygon[];
  overlay: "density-neutron" | "none";
  mode: "points" | "density";
  pointSize: number;
  colourFor: (index: number) => string;
  densityColour: (t: number) => string;
  showFit: boolean;
  showEdges: boolean;
  polygon: Vertex[];
  drawing: boolean;
  /** Alat gambar: poligon diklik sudut demi sudut; selebihnya diseret. */
  tool: ShapeTool;
  onVertex: (vertex: Vertex) => void;
  onClosePolygon: () => void;
  /** Bentuk utuh yang baru: hasil seretan sebuah alat, atau simpul yang disunting. */
  onPolygonChange: (polygon: Vertex[], finished: boolean) => void;
}) {
  const { x, y } = payload.points;

  /* Seretan yang sedang berlangsung: membangun bentuk (alat seret), atau
     memindahkan satu simpul bentuk yang sudah jadi. Disimpan di sini dan bukan
     di induknya, sebab pratinjau yang berganti tiap gerak tetikus tidak perlu
     menggambar ulang seluruh layar. */
  const [drag, setDrag] = useState<
    | { kind: "shape"; start: Vertex; path: Vertex[]; box: { width: number; height: number } }
    | { kind: "vertex"; index: number }
    | null
  >(null);
  const at = (event: { clientX: number; clientY: number; currentTarget: Element }) => {
    const box = event.currentTarget.getBoundingClientRect();
    return {
      point: { x: (event.clientX - box.left) / box.width, y: (event.clientY - box.top) / box.height },
      box: { width: box.width, height: box.height },
    };
  };
  const shapeFrom = (d: { start: Vertex; path: Vertex[]; box: { width: number; height: number } }) =>
    tool === "freehand"
      ? thinPath(d.path, d.box)
      : tool === "polygon"
        ? null
        : dragShape(tool, d.start, d.path[d.path.length - 1] ?? d.start, d.box);
  const preview = drag?.kind === "shape" ? shapeFrom(drag) : null;
  const editable = !drawing && polygon.length >= 3;

  const geometry = useMemo(() => {
    if (!axes) return null;
    // Skala tetap memotong: sebuah titik di luar kotak chart tidak bisa
    // digambar, dan menjepitnya ke tepi akan menumpuknya di sana sebagai
    // rombongan yang terbaca sebagai data. Yang di luar dilepas dan dihitung —
    // pembaca berhak tahu berapa banyak yang tidak dilihatnya.
    let outside = 0;
    const points = x.map((vx, index) => {
      const vy = y[index];
      if (!Number.isFinite(vx) || !Number.isFinite(vy)) return null;
      if ((axes.x.log && !(vx > 0)) || (axes.y.log && !(vy > 0))) return null;
      const tx = place(axes.x, vx);
      const ty = place(axes.y, vy);
      if (tx < 0 || tx > 1 || ty < 0 || ty > 1) {
        outside += 1;
        return null;
      }
      return { cx: PAD + tx * SPAN, cy: PAD + (1 - ty) * SPAN, index };
    });
    return { points, outside };
  }, [axes, x, y]);

  /**
   * Poligon tersimpan, dipasang kembali ke kotak gambar yang sekarang.
   *
   * Sisinya dibagi di ruang tempat ia digambar dulu — lihat `shapeVertices` —
   * supaya bentuk yang tampil selalu bentuk yang menggolongkan, juga ketika
   * seseorang memindahkan sumbunya dari linier ke logaritmik sesudahnya.
   */
  const savedShapes = useMemo(() => {
    if (!axes) return [];
    return saved
      .map((shape) => {
        const space = { logX: shape.log_x, logY: shape.log_y };
        // Simpul apa adanya, dipasang ke kotak — `steps` satu tidak membagi
        // sisi mana pun. Dipakai dua kali: sebagai jangkar nama, dan sebagai
        // garis luar ketika ruang simpannya memang ruang sumbu yang tampil.
        const corners = shapeVertices(shape.vertices, space, axes.x, axes.y, 1);
        if (!corners.length) return null;
        const sameSpace = shape.log_x === axes.x.log && shape.log_y === axes.y.log;
        // Membagi sebuah ruas lurus di ruangnya sendiri memulangkan ruas yang
        // sama persis, jadi di ruang yang sama itu enam belas kali lipat
        // perintah jalur untuk bentuk yang tidak berubah satu titik pun.
        const outline = sameSpace
          ? corners
          : shapeVertices(shape.vertices, space, axes.x, axes.y);
        if (!outline.length) return null;
        // Rata-rata **sudutnya**, bukan rata-rata garis luar yang sudah
        // dibagi: yang kedua menimbang tiap sisi sama berat berapa pun
        // panjangnya, dan namanya lalu melayang ke arah sisi yang pendek.
        const mid = corners.reduce(
          (sum, vertex) => ({ x: sum.x + vertex.x, y: sum.y + vertex.y }),
          { x: 0, y: 0 },
        );
        return {
          id: shape.id,
          name: shape.name,
          code: shape.class_value,
          color: shape.color,
          d: polygonPath(outline, true),
          cx: (100 * mid.x) / corners.length,
          cy: (100 * mid.y) / corners.length,
        };
      })
      .filter((shape): shape is NonNullable<typeof shape> => shape !== null);
  }, [axes, saved]);

  const cells = useMemo(() => {
    if (!axes || mode !== "density" || !payload.density) return [];
    const grid = payload.density;
    const dx = (grid.x1 - grid.x0) / grid.bins;
    const dy = (grid.y1 - grid.y0) / grid.bins;
    const peak = grid.max || 1;
    const out: { x: number; y: number; w: number; h: number; fill: string; count: number }[] = [];
    for (let row = 0; row < grid.counts.length; row++) {
      for (let col = 0; col < grid.counts[row].length; col++) {
        const count = grid.counts[row][col];
        if (count <= 0) continue;
        const left = placeMapped(axes.x, grid.x0 + col * dx);
        const right = placeMapped(axes.x, grid.x0 + (col + 1) * dx);
        const bottom = placeMapped(axes.y, grid.y0 + row * dy);
        const top = placeMapped(axes.y, grid.y0 + (row + 1) * dy);
        const x0 = PAD + Math.min(left, right) * SPAN;
        const x1 = PAD + Math.max(left, right) * SPAN;
        const y0 = PAD + (1 - Math.max(bottom, top)) * SPAN;
        const y1 = PAD + (1 - Math.min(bottom, top)) * SPAN;
        out.push({
          x: x0,
          y: y0,
          w: Math.max(0.1, x1 - x0),
          h: Math.max(0.1, y1 - y0),
          // Akar dan bukan bagi lurus: kerapatan sebuah awan log berekor
          // panjang, dan pembagian lurus mengecat hampir seluruh peta dengan
          // warna ujung terendah.
          fill: densityColour(Math.sqrt(count / peak)),
          count,
        });
      }
    }
    return out;
  }, [axes, mode, payload.density, densityColour]);

  /**
   * Garis bantu, dihitung dari nilai dan bukan dari sudut kotak gambar.
   *
   * Konstantanya milik proyek ini, bukan angka buku: garis matriks yang tidak
   * sepakat dengan porositas yang dihitung aplikasi ini sendiri menyesatkan
   * justru karena terlihat berwibawa.
   */
  const lines = useMemo(() => {
    if (!axes) return [];
    const c = payload.constants;
    const segment = (
      key: string,
      label: string,
      from: [number, number],
      to: [number, number],
      color: string,
      width: number,
      dash?: string,
    ) => {
      const at = (point: [number, number]) => [
        PAD + place(axes.x, point[0]) * SPAN,
        PAD + (1 - place(axes.y, point[1])) * SPAN,
      ];
      const [x1, y1] = at(from);
      const [x2, y2] = at(to);
      if (![x1, y1, x2, y2].every(Number.isFinite)) return null;
      return { key, label, d: `M${x1} ${y1} L${x2} ${y2}`, color, width, dash, ax: x2, ay: y2 };
    };

    if (overlay === "density-neutron") {
      const phiLo = Math.min(axes.x.lo, axes.x.hi);
      const phiHi = Math.max(axes.x.lo, axes.x.hi);
      const matrix = (phi: number) => c.rho_matrix - phi * (c.rho_matrix - c.rho_fluid);
      return [
        segment("quartz", "matrix line",
                [phiLo, matrix(phiLo)], [phiHi, matrix(phiHi)],
                "var(--ink-6)", 0.9, "3 2"),
        segment("shale", "shale line",
                [0, c.rho_matrix], [c.nphi_shale, c.rho_shale],
                "var(--amber)", 1.1),
      ].filter((line): line is NonNullable<typeof line> => line !== null);
    }

    return [];
  }, [axes, overlay, payload.constants]);

  /** Garis regresi, dalam ruang yang sama dengan tempat ia dipasang. */
  const fit = useMemo(() => {
    if (!axes || !showFit || !payload.regression) return null;
    const segment = fitSegment(payload.regression, axes.x.lo, axes.x.hi);
    if (!segment) return null;
    const at = (vx: number, vy: number) => [
      PAD + placeMapped(axes.x, vx) * SPAN,
      PAD + (1 - placeMapped(axes.y, vy)) * SPAN,
    ];
    const [x1, y1] = at(segment.x1, segment.y1);
    const [x2, y2] = at(segment.x2, segment.y2);
    if (![x1, y1, x2, y2].every(Number.isFinite)) return null;
    return { d: `M${x1} ${y1} L${x2} ${y2}`, ax: x2, ay: y2 };
  }, [axes, showFit, payload.regression]);

  const inside = useMemo(() => {
    if (polygon.length < 3 || !geometry) return null;
    const set = new Set<number>();
    for (const point of geometry.points) {
      if (point && pointInPolygon(point.cx / 100, point.cy / 100, polygon)) set.add(point.index);
    }
    return set;
  }, [polygon, geometry]);

  /**
   * Batang histogram tepi, dalam satuan kotak gambar.
   *
   * Letaknya dihitung lewat `placeMapped` dari **nilai** tiap tepi bin, persis
   * seperti sel kerapatan di atas, dan bukan dengan membagi kotaknya rata
   * sebanyak bin. Grid yang dihitung server berdiri pada rentangnya sendiri,
   * dan rentang itu tidak selalu rentang sumbu yang tergambar — seseorang yang
   * mengetik satu batas saja menggeser salah satunya tanpa menggeser yang lain.
   * Batang yang dibagi rata lalu melenceng dari puncak awan yang justru
   * dirangkumnya, dan selisihnya terbaca sebagai sifat datanya.
   */
  const edgeBars = useMemo(() => {
    if (!axes || !showEdges || !payload.density) return null;
    const grid = payload.density;
    const sums = marginals(grid);
    const dx = (grid.x1 - grid.x0) / grid.bins;
    const dy = (grid.y1 - grid.y0) / grid.bins;
    const span = (a: number, b: number) => [Math.min(a, b), Math.max(a, b)];
    return {
      peakX: sums.xMax,
      peakY: sums.yMax,
      x: sums.x.map((n, col) => {
        const [lo, hi] = span(placeMapped(axes.x, grid.x0 + col * dx),
                              placeMapped(axes.x, grid.x0 + (col + 1) * dx));
        return { at: PAD + lo * SPAN, size: (hi - lo) * SPAN, h: sums.xMax ? n / sums.xMax : 0, n };
      }),
      y: sums.y.map((n, row) => {
        const [lo, hi] = span(placeMapped(axes.y, grid.y0 + row * dy),
                              placeMapped(axes.y, grid.y0 + (row + 1) * dy));
        // Kotaknya menggambar y menaik ke bawah, jadi tepi atas batang adalah
        // kedudukan yang *besar*.
        return { at: PAD + (1 - hi) * SPAN, size: (hi - lo) * SPAN,
                 h: sums.yMax ? n / sums.yMax : 0, n };
      }),
    };
  }, [axes, showEdges, payload.density]);

  /**
   * Titik yang sedang ditunjuk kursor, atau null.
   *
   * Sebuah awan crossplot yang tidak bisa ditanyai "di mana titik ini" adalah
   * gambar dan bukan alat: sekelompok sampel yang duduk jauh dari awannya
   * sendiri hampir selalu satu selang pendek di satu sumur — washout, badan
   * lubang yang runtuh, satu kurva yang salah satuan — dan menemukannya berarti
   * membaca kedalamannya, bukan menebaknya dari nilai kurvanya di enam sumur.
   */
  const [hover, setHover] = useState<number | null>(null);

  const reading = useMemo(() => {
    if (hover === null || !geometry) return null;
    const spot = geometry.points[hover];
    if (!spot) return null;
    const points = payload.points;
    return {
      cx: spot.cx,
      cy: spot.cy,
      well: payload.wells[points.well[hover]]?.name ?? "",
      zone: points.zone[hover] ?? "",
      md: points.md?.[hover] ?? null,
      x: points.x[hover],
      y: points.y[hover],
    };
  }, [hover, geometry, payload]);

  const tickText = {
    font: "400 9px var(--font-mono)",
    color: "var(--ink-5)",
    position: "absolute" as const,
    whiteSpace: "nowrap" as const,
  };

  const plot = (
    <div
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        const { point, box } = at(event);
        if (drawing && tool !== "polygon") {
          event.currentTarget.setPointerCapture?.(event.pointerId);
          setDrag({ kind: "shape", start: point, path: [point], box });
          return;
        }
        if (!editable) return;
        const handle = nearestHandle(polygon, point, box);
        if (!handle) return;
        event.currentTarget.setPointerCapture?.(event.pointerId);
        if (handle.kind === "mid") {
          onPolygonChange(insertVertex(polygon, handle.index, point), false);
          setDrag({ kind: "vertex", index: handle.index + 1 });
        } else {
          setDrag({ kind: "vertex", index: handle.index });
        }
      }}
      onPointerMove={(event) => {
        if (!drag) return;
        const { point } = at(event);
        if (drag.kind === "shape") setDrag({ ...drag, path: [...drag.path, point] });
        else onPolygonChange(moveVertex(polygon, drag.index, point), false);
      }}
      onPointerUp={() => {
        if (drag?.kind === "shape") {
          const shape = shapeFrom(drag);
          if (shape && shape.length >= 3) onPolygonChange(shape, true);
        } else if (drag?.kind === "vertex") {
          onPolygonChange(polygon, true);
        }
        setDrag(null);
      }}
      onClick={(event) => {
        if (!drawing || tool !== "polygon") return;
        const box = event.currentTarget.getBoundingClientRect();
        onVertex({
          x: (event.clientX - box.left) / box.width,
          y: (event.clientY - box.top) / box.height,
        });
      }}
      onDoubleClick={(event) => {
        if (drawing) {
          if (tool === "polygon") onClosePolygon();
          return;
        }
        // Klik ganda pada sebuah simpul menghapusnya; segitiga tidak dikurangi lagi.
        if (!editable) return;
        const { point, box } = at(event);
        const handle = nearestHandle(polygon, point, box);
        if (handle?.kind === "vertex") onPolygonChange(removeVertex(polygon, handle.index), true);
      }}
      onMouseMove={(event) => {
        // Tidak sambil menggambar: di sana kursor sedang menaruh sudut, dan
        // sebuah label yang mengikutinya menutupi tempat sudut berikutnya.
        if (drawing || drag || mode !== "points" || !geometry) return;
        const box = event.currentTarget.getBoundingClientRect();
        const cx = ((event.clientX - box.left) / box.width) * 100;
        const cy = ((event.clientY - box.top) / box.height) * 100;
        let best: number | null = null;
        let nearest = Infinity;
        for (const point of geometry.points) {
          if (!point) continue;
          // Jaraknya diukur dalam piksel dan bukan dalam satuan viewBox.
          // Kotaknya memakai preserveAspectRatio="none", jadi satu satuan
          // mendatar dan satu satuan tegak adalah dua panjang yang berbeda —
          // dan pada kotak selebar dua kali tingginya, lingkaran pilihnya
          // menjadi lonjong sejauh itu pula.
          const dx = ((point.cx - cx) / 100) * box.width;
          const dy = ((point.cy - cy) / 100) * box.height;
          const gap = dx * dx + dy * dy;
          if (gap < nearest) {
            nearest = gap;
            best = point.index;
          }
        }
        setHover(nearest <= HOVER_RADIUS * HOVER_RADIUS ? best : null);
      }}
      onMouseLeave={() => setHover(null)}
      style={{
        position: "relative",
        flex: 1,
        minHeight: 140,
        background: "var(--canvas-deep)",
        border: "1px solid var(--line-strong)",
        borderRadius: "var(--radius-panel)",
        overflow: "hidden",
        boxShadow: "var(--shadow-panel)",
        cursor: drawing ? "crosshair" : drag?.kind === "vertex" ? "grabbing" : "default",
        touchAction: drawing || editable ? "none" : undefined,
      }}
    >
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        aria-label={`Crossplot ${payload.x_label} vs ${payload.y_label}`}
      >
        {axes?.x.ticks.map((tick) => (
          <line key={`gx${tick.value}`} x1={PAD + tick.t * SPAN} x2={PAD + tick.t * SPAN}
                y1={0} y2={100} stroke="var(--grid-line-strong)" strokeWidth={1}
                vectorEffect="non-scaling-stroke" />
        ))}
        {axes?.y.ticks.map((tick) => (
          <line key={`gy${tick.value}`} x1={0} x2={100}
                y1={PAD + (1 - tick.t) * SPAN} y2={PAD + (1 - tick.t) * SPAN}
                stroke="var(--grid-line-strong)" strokeWidth={1}
                vectorEffect="non-scaling-stroke" />
        ))}
        {mode === "density" &&
          cells.map((cell, index) => (
            <rect key={index} x={cell.x} y={cell.y} width={cell.w} height={cell.h} fill={cell.fill}>
              <title>{cell.count.toLocaleString("en-US")} samples</title>
            </rect>
          ))}
        {lines.map((line) => (
          <path
            key={line.key}
            d={line.d}
            stroke={line.color}
            strokeWidth={line.width}
            strokeDasharray={line.dash}
            fill="none"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {mode === "points" &&
          geometry?.points.map((point) =>
            point ? (
              <circle
                key={point.index}
                cx={point.cx}
                cy={point.cy}
                r={pointSize}
                fill={colourFor(point.index)}
                // Yang di luar poligon tidak dihapus melainkan diredupkan: awan
                // yang hilang sebagian membuat seleksi terlihat seperti seluruh
                // datanya.
                fillOpacity={inside ? (inside.has(point.index) ? 1 : 0.12) : 0.8}
              />
            ) : null,
          )}
        {fit && (
          <path d={fit.d} stroke="var(--accent-bright)" strokeWidth={1.3} fill="none"
                strokeDasharray="5 3" vectorEffect="non-scaling-stroke" />
        )}
        {savedShapes.map((shape) => (
          <path
            key={shape.id}
            d={shape.d}
            fill={shape.color}
            // Setipis ini dengan sengaja: poligon kelas berdiri di atas awan
            // yang justru ingin dilihat orang, dan isian yang pekat mengubah
            // sebuah batas menjadi tambalan yang menutupi batuannya sendiri.
            fillOpacity={0.1}
            stroke={shape.color}
            strokeWidth={1.1}
            vectorEffect="non-scaling-stroke"
          >
            <title>
              {shape.name} — class {shape.code}
            </title>
          </path>
        ))}
        {polygon.length > 0 && (
          <path
            d={polygonPath(polygon, polygon.length > 2 && !drawing)}
            fill={polygon.length > 2 && !drawing ? "var(--accent-wash)" : "none"}
            fillOpacity={0.25}
            stroke="var(--accent)"
            strokeWidth={1.2}
            strokeDasharray={drawing ? "4 2" : undefined}
            vectorEffect="non-scaling-stroke"
          />
        )}
        {preview && preview.length > 1 && (
          <path
            d={polygonPath(preview, tool !== "freehand")}
            fill="var(--accent-wash)"
            fillOpacity={0.2}
            stroke="var(--accent)"
            strokeWidth={1.2}
            strokeDasharray="4 2"
            vectorEffect="non-scaling-stroke"
          />
        )}
        {/* Pegangan penyuntingan: simpul terisi, titik tengah sisi berongga.
            Bentuk bulat berisi 48 simpul, jadi simpulnya hanya digambar bila
            tidak terlalu rapat — pegangannya tetap bisa diraih. */}
        {editable && polygon.length <= 24 &&
          midpoints(polygon).map((m, index) => (
            <circle key={`m${index}`} cx={m.x * 100} cy={m.y * 100} r={0.55}
                    fill="var(--canvas-deep)" stroke="var(--accent)" strokeWidth={0.8}
                    vectorEffect="non-scaling-stroke" />
          ))}
        {polygon.map((vertex, index) => (
          <circle key={index} cx={vertex.x * 100} cy={vertex.y * 100}
                  r={polygon.length > 24 ? 0.45 : 0.9} fill="var(--accent)" />
        ))}
        {reading && (
          <circle
            cx={reading.cx}
            cy={reading.cy}
            r={Math.max(2, pointSize * 2)}
            fill="none"
            stroke="var(--accent-bright)"
            strokeWidth={1.4}
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>
      {lines.map((line) => {
        const nearRight = line.ax > 68;
        return (
          <Tag
            key={`t${line.key}`}
            color={line.color}
            {...(nearRight
              ? { right: `${Math.max(1, 100 - line.ax)}%` }
              : { left: `${Math.max(1, line.ax)}%` })}
            top={`${Math.max(4, Math.min(92, line.ay))}%`}
            transform={`translate(${nearRight ? "-6px" : "6px"}, -50%)`}
          >
            {line.label}
          </Tag>
        );
      })}
      {/* Nama poligon ditulis sebagai HTML dan bukan <text> di dalam SVG-nya,
          dengan alasan yang sama seperti angka sumbu di bawah: kotak itu
          memakai preserveAspectRatio="none", dan huruf yang ikut diregangkan
          berubah lebar setiap kali panelnya digeser. */}
      {savedShapes.map((shape) => (
        <Tag
          key={`n${shape.id}`}
          color={shape.color}
          left={`${Math.max(1, Math.min(88, shape.cx))}%`}
          top={`${Math.max(3, Math.min(94, shape.cy))}%`}
          transform="translate(-50%, -50%)"
        >
          {shape.name}
        </Tag>
      ))}
      {fit && payload.regression && (
        <Tag color="var(--accent-bright)" left="2%" top="4%">
          R² {payload.regression.r2?.toFixed(3) ?? "—"} · n{" "}
          {payload.regression.n.toLocaleString("en-US")}
        </Tag>
      )}
      {/* Pembacaan titik. Ia berpindah ke seberang kursor begitu titiknya
          duduk di separuh kanan atau separuh bawah kotak: label yang selalu
          muncul di kanan-bawah menggantung di luar kotak justru pada sudut yang
          paling sering ditanyai orang — kaki serpih sebuah awan
          densitas–neutron ada di sana. */}
      {reading && (
        <Tag
          color="var(--ink-3)"
          {...(reading.cx > 55
            ? { right: `${Math.max(1, 100 - reading.cx)}%` }
            : { left: `${Math.max(1, reading.cx)}%` })}
          {...(reading.cy > 55
            ? { bottom: `${Math.max(1, 100 - reading.cy)}%` }
            : { top: `${Math.max(1, reading.cy)}%` })}
          transform={`translate(${reading.cx > 55 ? "-10px" : "10px"}, ${
            reading.cy > 55 ? "-10px" : "10px"
          })`}
        >
          {reading.well}
          {reading.md === null ? "" : ` · ${reading.md.toFixed(1)} m`}
          {reading.zone ? ` · ${reading.zone}` : ""}
          {` · ${xLabel} ${formatReading(reading.x)} · ${yLabel} ${formatReading(reading.y)}`}
        </Tag>
      )}
      {geometry && geometry.outside > 0 && mode === "points" && (
        <Tag left={10} bottom={9} color="var(--ink-5)">
          {geometry.outside.toLocaleString("en-US")} outside the scale
        </Tag>
      )}
      {!geometry && (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
          <span style={{ font: "400 11px var(--font-mono)", color: "var(--ink-6)" }}>
            No point passes the filter.
          </span>
        </div>
      )}
    </div>
  );

  /* Sepasang kolom yang menyela tiap baris supaya semuanya tetap sejajar:
     lebar tanda sumbu Y di kiri, lebar bilah histogram Y di kanan. */
  const gutter = (side: "left" | "right") =>
    side === "left" ? (
      <div style={{ width: 34, flex: "none" }} />
    ) : edgeBars ? (
      <div style={{ width: EDGE_STRIP, flex: "none" }} />
    ) : null;

  // Angka sumbu adalah HTML dan bukan <text> di dalam SVG-nya. Kotak gambar itu
  // memakai preserveAspectRatio="none" supaya awan titik mengisi berapa pun
  // ruang yang ada, dan huruf yang ikut diregangkan tidak sebanding lebarnya
  // dari satu panel ke panel berikutnya.
  //
  // Tanda sumbu Y duduk di baris yang sama dengan kotak gambar, dan bukan di
  // sebuah kolom setinggi seluruh bagan. Persentase letaknya dihitung terhadap
  // tinggi induknya, jadi kolom yang lebih tinggi daripada kotaknya menaruh
  // tiap tanda sedikit di bawah garis kisi yang dinamainya — dan selisih itu
  // tumbuh persis sebesar apa pun yang ditambahkan di bawah atau di atas kotak.
  return (
    <div style={{ display: "flex", gap: 6, flex: 1, minHeight: 0 }}>
      <div style={{ display: "flex", alignItems: "center", flex: "none" }}>
        <span
          style={{
            font: "500 10px var(--font-mono)",
            color: "var(--ink-4)",
            transform: "rotate(-90deg)",
            whiteSpace: "nowrap",
          }}
        >
          {yLabel}
          {axes?.y.log ? " · log" : ""}
        </span>
      </div>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
        {edgeBars && (
          <div style={{ display: "flex", gap: 6, flex: "none", height: EDGE_STRIP }}>
            {gutter("left")}
            <EdgeStrip bars={edgeBars.x} peak={edgeBars.peakX} axis="x" label={xLabel} />
            {gutter("right")}
          </div>
        )}
        <div style={{ display: "flex", gap: 6, flex: 1, minHeight: 0 }}>
          <div style={{ width: 34, flex: "none", position: "relative" }}>
            {axes?.y.ticks.map((tick) => (
              <span
                key={tick.value}
                style={{
                  ...tickText,
                  right: 0,
                  top: `${PAD + (1 - tick.t) * SPAN}%`,
                  transform: "translateY(-50%)",
                }}
              >
                {formatTick(axes.y, tick.value)}
              </span>
            ))}
          </div>
          {plot}
          {edgeBars && (
            <div style={{ width: EDGE_STRIP, flex: "none", display: "flex" }}>
              <EdgeStrip bars={edgeBars.y} peak={edgeBars.peakY} axis="y" label={yLabel} />
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, flex: "none" }}>
          {gutter("left")}
          <div style={{ flex: 1, minWidth: 0, position: "relative", height: 12 }}>
            {axes?.x.ticks.map((tick) => (
              <span
                key={tick.value}
                style={{ ...tickText, left: `${PAD + tick.t * SPAN}%`, transform: "translateX(-50%)" }}
              >
                {formatTick(axes.x, tick.value)}
              </span>
            ))}
          </div>
          {gutter("right")}
        </div>
        <div style={{ display: "flex", gap: 6, flex: "none" }}>
          {gutter("left")}
          <span
            style={{
              flex: 1,
              minWidth: 0,
              font: "500 10px var(--font-mono)",
              color: "var(--ink-4)",
              textAlign: "center",
            }}
          >
            {xLabel}
            {axes?.x.log ? " · log" : ""}
          </span>
          {gutter("right")}
        </div>
      </div>
    </div>
  );
}

/** Tinggi bilah histogram X, dan lebar bilah histogram Y. */
const EDGE_STRIP = 46;

/**
 * Sebuah histogram tepi: sebaran satu sumbu, sejajar dengan sumbu itu.
 *
 * Batangnya tumbuh dari sisi yang **menempel** pada kotak gambar, dan bukan
 * dari sisi luarnya. Sebuah histogram yang menggantung terbalik di atas awannya
 * memaksa mata membalik arah setiap kali ia berpindah dari satu ke yang lain,
 * padahal keduanya berdampingan justru supaya bisa dibaca sekaligus.
 */
function EdgeStrip({
  bars,
  peak,
  axis,
  label,
}: {
  bars: { at: number; size: number; h: number; n: number }[];
  peak: number;
  axis: "x" | "y";
  label: string;
}) {
  return (
    <div
      style={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        background: "var(--canvas-deep)",
        border: "1px solid var(--line-soft)",
        borderRadius: "var(--radius-panel)",
        overflow: "hidden",
      }}
    >
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        aria-label={`Distribution of ${label}, ${peak.toLocaleString("en-US")} samples in the tallest bin`}
      >
        {bars.map((bar, index) =>
          bar.h <= 0 ? null : axis === "x" ? (
            <rect
              key={index}
              x={bar.at}
              // Sekurangnya setipis ini: bin yang lebarnya membulat menjadi nol
              // pada sumbu logaritmik hilang sama sekali, dan yang hilang di
              // ujung sempit sebuah sumbu log justru bin yang paling berisi.
              width={Math.max(0.25, bar.size)}
              y={100 - bar.h * 100}
              height={bar.h * 100}
              fill="var(--accent)"
              fillOpacity={0.5}
            >
              <title>{bar.n.toLocaleString("en-US")} samples</title>
            </rect>
          ) : (
            <rect
              key={index}
              y={bar.at}
              height={Math.max(0.25, bar.size)}
              x={0}
              width={bar.h * 100}
              fill="var(--accent)"
              fillOpacity={0.5}
            >
              <title>{bar.n.toLocaleString("en-US")} samples</title>
            </rect>
          ),
        )}
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------ Pelengkap */

/**
 * Statistik titik yang dilingkari.
 *
 * Dihitung dari titik yang *tergambar*, dan mengatakannya sendiri. Poligon
 * hanya bisa menyentuh apa yang ada di layar; ringkasan seluruh data dihitung
 * server. Menyebut cacah seleksi sebagai cacah data akan melaporkan seperlima
 * dari sebuah awan yang sudah dijarangkan sebagai seperlima batuannya.
 */
function SelectionStats({
  payload,
  polygon,
  axes,
  xLabel,
  yLabel,
}: {
  payload: FieldCrossplot | null;
  polygon: Vertex[];
  /** Sumbu yang sama dengan yang dipakai menggambar — lihat induknya. */
  axes: { x: Axis; y: Axis } | null;
  xLabel: string;
  yLabel: string;
}) {
  const result = useMemo(() => {
    if (!payload || !axes || polygon.length < 3) return null;
    const { x, y } = payload.points;
    const xs: number[] = [];
    const ys: number[] = [];
    for (let i = 0; i < x.length; i++) {
      const vx = x[i];
      const vy = y[i];
      if (!Number.isFinite(vx) || !Number.isFinite(vy)) continue;
      if ((axes.x.log && !(vx > 0)) || (axes.y.log && !(vy > 0))) continue;
      const cx = (PAD + place(axes.x, vx) * SPAN) / 100;
      const cy = (PAD + (1 - place(axes.y, vy)) * SPAN) / 100;
      if (!pointInPolygon(cx, cy, polygon)) continue;
      xs.push(vx);
      ys.push(vy);
    }
    return { x: summarise(xs), y: summarise(ys), n: xs.length, shown: payload.points.shown };
  }, [payload, polygon, axes]);

  if (!result || !result.x || !result.y) return null;
  const share = result.shown ? (100 * result.n) / result.shown : 0;
  return (
    <>
      <KeyValue k="Picked" v={`${result.n.toLocaleString("en-US")} points`} />
      <KeyValue k="Bagian" v={`${share.toFixed(1)}%`} />
      <KeyValue k={`${xLabel} mean`} v={result.x.mean.toFixed(4)} />
      <KeyValue k={`${xLabel} p50`} v={result.x.p50.toFixed(4)} />
      <KeyValue k={`${yLabel} mean`} v={result.y.mean.toFixed(4)} />
      <KeyValue k={`${yLabel} p50`} v={result.y.p50.toFixed(4)} />
      <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
        Computed from the points drawn. A decimated cloud keeps its shape, but the count is the
        count of the sample — not the count of rock samples.
      </span>
    </>
  );
}

/**
 * Legenda: warna sumur, warna zona, bar warna kurva — atau skala kerapatan.
 *
 * Yang terakhir bukan tambahan melainkan koreksi. Pada mode kerapatan warna
 * tidak lagi menyatakan sumur atau zona melainkan *cacah*, dan legenda sumur
 * yang tetap berdiri di bawahnya membuat peta kerapatan terbaca sebagai peta
 * sumur: sebuah keterangan yang salah, dan yang salah justru karena ia
 * lengkap dan rapi.
 */
function Legend({
  payload,
  colourBy,
  zoneColors,
  wellColors,
  scale,
  depthScale,
  mode,
  densityColour,
}: {
  payload: FieldCrossplot;
  colourBy: ColourBy;
  zoneColors: Map<string, string>;
  wellColors: string[];
  scale: ColorScale;
  depthScale: ColorScale;
  mode: PlotMode;
  densityColour: (t: number) => string;
}) {
  if (mode === "density") {
    if (!payload.density) return null;
    const steps = Array.from({ length: 24 }, (_, i) => i / 23);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
        <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>1</span>
        <div style={{ display: "flex", flex: 1, height: 8, borderRadius: 3, overflow: "hidden" }}>
          {steps.map((t) => (
            <span key={t} style={{ flex: 1, background: densityColour(Math.sqrt(t)) }} />
          ))}
        </div>
        <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
          {payload.density.max.toLocaleString("en-US")}
        </span>
        <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-5)" }}>
          sampel per sel
        </span>
      </div>
    );
  }

  if (colourBy === "depth") {
    if (!payload.md_range) return null;
    const painter = makePainter(depthScale, payload.md_range[0], payload.md_range[1]);
    const steps = Array.from({ length: 24 }, (_, i) => i / 23);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
        <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
          {painter.lo.toFixed(0)}
        </span>
        <div style={{ display: "flex", flex: 1, height: 8, borderRadius: 3, overflow: "hidden" }}>
          {steps.map((t) => (
            <span key={t} style={{ flex: 1, background: painter.atFraction(t) }} />
          ))}
        </div>
        <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
          {painter.hi.toFixed(0)}
        </span>
        <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-5)" }}>MD</span>
      </div>
    );
  }

  if (colourBy === "curve") {
    if (!payload.points.z) return null;
    const painter = makePainter(scale, payload.points.z_min ?? 0, payload.points.z_max ?? 1);
    const steps = Array.from({ length: 24 }, (_, i) => i / 23);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
        <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
          {painter.lo.toFixed(2)}
        </span>
        <div style={{ display: "flex", flex: 1, height: 8, borderRadius: 3, overflow: "hidden" }}>
          {steps.map((t) => (
            <span key={t} style={{ flex: 1, background: painter.atFraction(t) }} />
          ))}
        </div>
        <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
          {painter.hi.toFixed(2)}
        </span>
        <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-5)" }}>
          {payload.z_label}
        </span>
      </div>
    );
  }

  const rows =
    colourBy === "well"
      ? payload.wells.map((w, index) => ({
          key: String(w.id),
          label: w.name,
          color: wellColors[index] ?? "#8b939e",
          n: w.count,
        }))
      : [...new Set(payload.points.zone)]
          // Kedalaman yang tidak berada di dalam zona mana pun tetap tergambar,
          // dan tanpa barisnya sendiri di legenda ia menjadi segumpal titik
          // abu-abu yang tidak dijelaskan apa pun.
          .sort((a, b) => (a ? 0 : 1) - (b ? 0 : 1))
          .map((name) => ({
            key: name || "tanpa-zona",
            label: name || "no zone",
            color: zoneColors.get(name) ?? "#8b939e",
            n: payload.points.zone.filter((z) => z === name).length,
          }));

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", flex: "none" }}>
      {rows.map((row) => (
        <span
          key={row.key}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            font: "400 9.5px var(--font-mono)",
            color: "var(--ink-5)",
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: 2, background: row.color }} />
          {row.label}
          <span style={{ color: "var(--ink-7)" }}>{row.n.toLocaleString("en-US")}</span>
        </span>
      ))}
    </div>
  );
}

function LabelledDropdown({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>{label}</span>
      <Dropdown
        value={value}
        options={options.length ? options : [{ value, label: value }]}
        onChange={onChange}
        width="100%"
      />
    </div>
  );
}

function Tag({
  children,
  color,
  left,
  right,
  top,
  bottom,
  transform,
}: {
  children: React.ReactNode;
  color: string;
  /** Piksel dari tepi, atau persen kotaknya — label garis memakai yang kedua. */
  left?: number | string;
  right?: number | string;
  top?: number | string;
  bottom?: number | string;
  transform?: string;
}) {
  return (
    <span
      style={{
        position: "absolute",
        left,
        right,
        top,
        bottom,
        transform,
        padding: "3px 7px",
        background: "var(--panel)",
        border: "1px solid var(--line-strong)",
        borderRadius: 5,
        font: "400 9.5px var(--font-mono)",
        color,
      }}
    >
      {children}
    </span>
  );
}
